import { useState } from "react";
import { motion } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { X, ExternalLink, ShieldCheck } from "lucide-react";

/**
 * Verified credentials gallery.
 * Certificates ordered by tier: Statutory · Project Management ·
 * Process Excellence · Engineering · Continuing Ed. · Recognition.
 * Add new certs by appending to CERTIFICATES.
 */
const CERTIFICATES = [
  // === STATUTORY (top priority for board mandates) ===
  {
    id: "iddb",
    tier: "Statutory",
    tag: "Independent Director · Proficiency + Registration",
    title: "IICA Independent Director Databank",
    issuer: "Indian Institute of Corporate Affairs · Ministry of Corporate Affairs, GoI",
    date: "Registered · Apr 2026",
    credId: "IDDB-NR-202603-091456",
    image: "/certificates/iddb.jpg",
    pdf: "/certificates/iddb.pdf",
    featured: true,
  },

  // === PROJECT MANAGEMENT ===
  {
    id: "pmp",
    tier: "Project Management",
    tag: "PMP®",
    title: "Project Management Professional",
    issuer: "Project Management Institute (PMI)",
    date: "Issued · Jun 2023",
    credId: "3543879",
    image: "/certificates/pmp.jpg",
    pdf: "/certificates/pmp.pdf",
    featured: true,
  },
  {
    id: "pmi-acp",
    tier: "Project Management",
    tag: "PMI-ACP®",
    title: "PMI Agile Certified Practitioner",
    issuer: "Project Management Institute (PMI)",
    date: "Issued · Jun 2023",
    credId: "3549451",
    image: "/certificates/pmi_acp.jpg",
    pdf: "/certificates/pmi_3.pdf",
    featured: true,
  },
  {
    id: "psm-i",
    tier: "Project Management",
    tag: "PSM I",
    title: "Professional Scrum Master I",
    issuer: "Scrum.org",
    date: "Issued · Jul 2023",
    credId: "981283",
    image: "/certificates/psm_i.jpg",
    pdf: "/certificates/psm_i.pdf",
    featured: false,
  },

  // === ENGINEERING / STATUTORY PROFESSIONAL ===
  {
    id: "ceng-fie",
    tier: "Engineering",
    tag: "Chartered Engineer · FIE",
    title: "Fellow · Institution of Engineers (India)",
    issuer: "The Institution of Engineers (India)",
    date: "Issued · May 2023",
    credId: "M-1780705",
    image: "/certificates/chartered_engineer.jpg",
    pdf: "/certificates/chartered_engineer.pdf",
    featured: false,
  },

  // === PROCESS EXCELLENCE — Six Sigma (AIGPE) ===
  {
    id: "ssbb",
    tier: "Process Excellence",
    tag: "Six Sigma · Black Belt",
    title: "AIGPE Lean Six Sigma Black Belt",
    issuer: "Advanced Innovation Group Pro Excellence (AIGPE)",
    date: "Issued · Jun 2024",
    credId: "ZSSBB121145266",
    image: "/certificates/ssbb.jpg",
    pdf: "/certificates/ssbb.pdf",
    featured: false,
  },
  {
    id: "ssgb",
    tier: "Process Excellence",
    tag: "Six Sigma · Green Belt",
    title: "AIGPE Lean Six Sigma Green Belt",
    issuer: "Advanced Innovation Group Pro Excellence (AIGPE)",
    date: "Issued · Jun 2024",
    credId: "ZSSGB121151576",
    image: "/certificates/six_sigma.jpg",
    pdf: "/certificates/six_sigma.pdf",
    featured: false,
  },
  {
    id: "ssyb",
    tier: "Process Excellence",
    tag: "Six Sigma · Yellow Belt",
    title: "AIGPE Lean Six Sigma Yellow Belt",
    issuer: "Advanced Innovation Group Pro Excellence (AIGPE)",
    date: "Issued · Jun 2024",
    credId: "ZSSYB121156657",
    image: "/certificates/ssyb.jpg",
    pdf: "/certificates/ssyb.pdf",
    featured: false,
  },
  {
    id: "sswb",
    tier: "Process Excellence",
    tag: "Six Sigma · White Belt",
    title: "AIGPE Lean Six Sigma White Belt",
    issuer: "Advanced Innovation Group Pro Excellence (AIGPE)",
    date: "Issued · May 2024",
    credId: "ZSSWB121163453",
    image: "/certificates/sswb.jpg",
    pdf: "/certificates/sswb.pdf",
    featured: false,
  },
  {
    id: "spc",
    tier: "Process Excellence",
    tag: "Project Charter Specialist",
    title: "AIGPE Certified Project Charter Specialist",
    issuer: "Advanced Innovation Group Pro Excellence (AIGPE)",
    date: "Issued · May 2024",
    credId: "ZSSPC121143670",
    image: "/certificates/spc.jpg",
    pdf: "/certificates/spc.pdf",
    featured: false,
  },

  // === CONTINUING EDUCATION ===
  {
    id: "harvardx-leadership",
    tier: "Continuing Ed.",
    tag: "HarvardX · Leadership",
    title: "Exercising Leadership: Foundational Principles",
    issuer: "HarvardX (edX)",
    date: "Issued · Jul 2024",
    credId: "d0518a65124443faa5e536546c382011",
    image: "/certificates/harvardx_leadership.jpg",
    pdf: "/certificates/harvardx_leadership.pdf",
    featured: false,
  },
  {
    id: "alison-risk",
    tier: "Continuing Ed.",
    tag: "Risk Management · Diploma",
    title: "Diploma in Risk Management",
    issuer: "Alison",
    date: "Issued · May 2024",
    credId: "1406-39465350",
    image: "/certificates/alison_risk.jpg",
    pdf: "/certificates/alison_risk.pdf",
    featured: false,
  },
  {
    id: "agile-foundations",
    tier: "Continuing Ed.",
    tag: "Agile Foundations",
    title: "Agile Foundations",
    issuer: "LinkedIn Learning",
    date: "Issued · Jan 2024",
    credId: "3c4e9e64c38ec4c9…",
    image: "/certificates/agile_foundations.jpg",
    pdf: "/certificates/agile_foundations.pdf",
    featured: false,
  },
  {
    id: "pmi-gov",
    tier: "Continuing Ed.",
    tag: "Government Projects · PDU",
    title: "Project Management: Government Projects",
    issuer: "LinkedIn Learning · PMI-approved PDU",
    date: "Issued · Jan 2024",
    credId: "3043acb1584ed4d3…",
    image: "/certificates/pmi_gov.jpg",
    pdf: "/certificates/pmi_a.pdf",
    featured: false,
  },

  // === RECOGNITION ===
  {
    id: "tribal-quiz",
    tier: "Recognition",
    tag: "Certificate of Participation",
    title: "Dharti Aaba Bhagwan Birsa Munda – Janjatiya Nayak Ji Quiz",
    issuer: "Ministry of Tribal Affairs · MyGov, GoI",
    date: "Participation",
    credId: "GoI · MyGov",
    image: "/certificates/tribal_participation.jpg",
    pdf: null,
    featured: false,
  },
];

const TIERS = [
  "All",
  "Statutory",
  "Project Management",
  "Engineering",
  "Process Excellence",
  "Continuing Ed.",
  "Recognition",
];

const cardVariant = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] },
  }),
};

function CertificateCard({ cert, index, large }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <motion.button
          type="button"
          data-testid={`cert-open-${cert.id}`}
          custom={index}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          variants={cardVariant}
          className="group text-left bg-ink border border-surface-2 hover:border-brass transition-colors duration-500 overflow-hidden"
        >
          <div className={`relative overflow-hidden bg-[#0d1b30] ${large ? "aspect-[4/3]" : "aspect-[4/3]"}`}>
            <img
              src={cert.image}
              alt={cert.title}
              className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.04]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
            {cert.featured && (
              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 border border-brass text-brass bg-ink/70 backdrop-blur px-2.5 py-1 text-[9px] uppercase tracking-[0.22em]">
                <ShieldCheck size={11} /> Verified
              </span>
            )}
            <span className="absolute bottom-3 right-3 text-[9px] uppercase tracking-[0.22em] text-brass bg-ink/80 backdrop-blur px-2.5 py-1">
              {cert.tier}
            </span>
          </div>
          <div className={`${large ? "p-6 md:p-7" : "p-5"}`}>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-2">{cert.tag}</div>
            <h3 className={`font-serif-display text-ivory font-light leading-tight transition-colors duration-500 group-hover:text-brass ${large ? "text-xl md:text-2xl" : "text-base md:text-lg"}`}>
              {cert.title}
            </h3>
            <div className="mt-2 text-mutedfg text-xs leading-relaxed line-clamp-2">{cert.issuer}</div>
            <div className="mt-4 flex items-baseline justify-between border-t border-surface-2 pt-3">
              <span className="text-[10px] uppercase tracking-[0.22em] text-mutedfg tabular">{cert.date}</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-brass tabular">View →</span>
            </div>
          </div>
        </motion.button>
      </DialogTrigger>
      <DialogContent
        className="max-w-5xl w-[95vw] max-h-[95vh] overflow-y-auto p-0 bg-ink border border-surface-2 text-ivory rounded-none [&>button]:hidden"
        data-testid={`cert-dialog-${cert.id}`}
      >
        <DialogTitle className="sr-only">{cert.title}</DialogTitle>
        <DialogDescription className="sr-only">
          Certificate issued by {cert.issuer}. Credential ID {cert.credId}. {cert.date}.
        </DialogDescription>
        <div className="sticky top-0 z-10 bg-ink border-b border-surface-2 px-6 md:px-10 py-5 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass">{cert.tag}</div>
            <div className="mt-1 font-serif-display text-ivory text-xl md:text-2xl font-light leading-tight">
              {cert.title}
            </div>
            <div className="mt-1 text-mutedfg text-xs">{cert.issuer}</div>
          </div>
          <DialogClose asChild>
            <button
              type="button"
              data-testid={`cert-close-${cert.id}`}
              aria-label="Close"
              className="text-mutedfg hover:text-brass transition-colors shrink-0"
            >
              <X size={22} />
            </button>
          </DialogClose>
        </div>
        <div className="p-4 md:p-8 bg-[#0d1b30]">
          <img src={cert.image} alt={cert.title} className="w-full h-auto max-h-[70vh] object-contain mx-auto" />
        </div>
        <div className="px-6 md:px-10 py-6 border-t border-surface-2 grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-1">Issued</div>
            <div className="text-ivory font-light">{cert.date}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-brass mb-1">Credential ID</div>
            <div className="text-ivory font-light font-mono text-xs break-all">{cert.credId}</div>
          </div>
          {cert.pdf && (
            <div className="md:text-right">
              <a
                href={cert.pdf}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`cert-pdf-${cert.id}`}
                className="inline-flex items-center gap-2 border border-brass px-5 py-3 text-brass hover:bg-brass hover:text-ink text-[11px] uppercase tracking-[0.22em] transition-colors duration-500"
              >
                Open PDF <ExternalLink size={12} />
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Certificates() {
  const [filter, setFilter] = useState("All");

  const featured = CERTIFICATES.filter((c) => c.featured);
  const rest = CERTIFICATES.filter((c) => !c.featured);
  const filteredRest = filter === "All" ? rest : rest.filter((c) => c.tier === filter);
  const filteredFeatured = filter === "All" || featured.some((c) => c.tier === filter)
    ? (filter === "All" ? featured : featured.filter((c) => c.tier === filter))
    : [];

  return (
    <section id="certificates" className="relative border-b border-surface-2 bg-[#0b1a30]">
      <div className="mx-auto max-w-[1440px] px-6 md:px-16 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-6 items-end mb-14">
          <div className="col-span-12 md:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.28em] text-brass">
              <span className="tabular">12·</span><span className="ml-3">Verified Credentials</span>
            </div>
            <h2 className="mt-4 font-serif-display font-light text-ivory text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
              Documented, <span className="italic text-brass">verifiable</span>, on the record.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 md:col-start-9 text-mutedfg text-sm md:text-base font-light leading-relaxed">
            <span className="text-ivory tabular">{CERTIFICATES.length}</span> certificates uploaded and
            individually verifiable. Click any card to view the original with credential ID, issuer and date.
            IDDB registration is the statutory prerequisite for appointment as an Independent Director on
            Indian boards.
          </div>
        </div>

        {/* Tier filter */}
        <div className="mb-10 flex flex-wrap items-center gap-2">
          {TIERS.map((t) => {
            const active = filter === t;
            return (
              <button
                key={t}
                type="button"
                data-testid={`cert-filter-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(t)}
                className={`text-[10px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors duration-500 ${
                  active
                    ? "border-brass text-ink bg-brass"
                    : "border-surface-2 text-ivory/75 hover:border-brass hover:text-brass"
                }`}
              >
                {t}
              </button>
            );
          })}
        </div>

        {/* Featured cards */}
        {filteredFeatured.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {filteredFeatured.map((c, i) => (
              <CertificateCard key={c.id} cert={c} index={i} large />
            ))}
          </div>
        )}

        {/* Rest */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {filteredRest.map((c, i) => (
            <CertificateCard key={c.id} cert={c} index={i + filteredFeatured.length} />
          ))}
        </div>

        {filteredFeatured.length === 0 && filteredRest.length === 0 && (
          <div className="text-mutedfg text-sm py-10 text-center">
            No certificates in this tier yet.
          </div>
        )}

        <p className="mt-12 text-mutedfg text-xs md:text-sm max-w-3xl leading-relaxed">
          <span className="text-brass">Note —</span> Additional continuing-education records (IIT Roorkee EV,
          IIM Ahmedabad Leadership, Oracle Gen-AI, ISC2 Cybersecurity, EMBA Finance in progress) are
          maintained on LinkedIn. The LinkedIn profile in the footer is the master record.
        </p>
      </div>
    </section>
  );
}
